import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Job, actions } from '@kanda-libs/ks-frontend-services';
import { AsyncThunkActionError } from '@kanda-libs/ks-frontend-services/src/store/types';

import { useAppDispatch } from 'components/App';
import { URLS } from 'config';
import useApiError from 'hooks/useApiError';
import { useStatusContext } from 'pages/Status/context';
import { formatProvider, hasAppliedSecondLine } from 'utils';

interface DeclinedHook {
  failureUrl?: string;
  companyName: string;
  hasAppliedSecond: boolean;
  provider: string;
  showSecondLine: boolean;
  errorMessage?: string;
  onClick: () => void;
  isLoading: boolean;
  isSubmitting: boolean;
}

const shouldJobReapply = (job: Job) => {
  if (!job) return false;
  if (!job.customer_options) return false;
  return job.status === 'sent' && job.finance_status === 'finance_not_approved';
};

export default function useDeclined(): DeclinedHook {
  const { job, credit, jobCompanyInfo, provider, isLoading } =
    useStatusContext();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<AsyncThunkActionError | undefined>(
    undefined,
  );

  const { push } = useHistory();
  const dispatch = useAppDispatch();
  const handleApiError = useApiError('Unkown error requesting second line');

  const failureUrl = useMemo(() => job?.redirect_urls?.failure_url, [job]);

  const companyName = useMemo(() => {
    if (!jobCompanyInfo || isLoading) return 'Your tradesperson';
    return jobCompanyInfo?.company_name || 'Your tradesperson';
  }, [jobCompanyInfo, isLoading]);

  // const showSecondLine = useMemo(() => {
  //   if (!job || !credit) return false;
  //   if (provider === 'propensio') return false;
  //   const hasApplied = hasAppliedSecondLine(job, credit);
  //   return !hasApplied;
  // }, [job, credit, provider]);
  const showSecondLine = false;

  const formattedProvider = useMemo(() => {
    if (!provider) return '';
    if (provider === 'example') return 'example';
    return formatProvider(provider);
  }, [provider]);

  const errorMessage = useMemo(() => {
    if (!error) return undefined;
    const { code } = error;
    if (code !== 423) return undefined;
    return 'Unfortunately, no second line rates are available for this job at this time';
  }, [error]);

  const onClick = useCallback(() => {
    if (!job) return;
    const id = job?.id;
    if (!id) return;
    const shouldReapply = shouldJobReapply(job);
    if (!shouldReapply) return;
    setIsSubmitting(true);
    dispatch(
      actions.reapplyJob({
        body: {},
        params: { id },
        forceReload: true,
        onSuccess: () => {
          setIsSubmitting(false);
          push(URLS.reapply.replace(':id?', id));
        },
        onError: (currentError) => {
          setIsSubmitting(false);
          handleApiError(currentError);
          setError(currentError);
        },
      }),
    );
  }, [dispatch, handleApiError, job, push]);

  const hasAppliedSecond = useMemo(() => {
    if (!job) return false;
    return hasAppliedSecondLine(job, credit);
  }, [job, credit]);

  const canAppliedSecond = useMemo(() => {
    if (!job) return false;
    const hasApplied = hasAppliedSecondLine(job, credit);
    return !hasApplied && provider !== 'propensio';
  }, [job, credit, provider]);

  const timerRef = useRef<NodeJS.Timer | undefined>();

  const startTimeout = useCallback(() => {
    clearTimeout(timerRef.current as NodeJS.Timeout);
    timerRef.current = setTimeout(() => {
      window.location.assign(failureUrl as string);
    }, 10000);
  }, [failureUrl]);

  useEffect(() => {
    if (failureUrl && canAppliedSecond && hasAppliedSecond) startTimeout();
    return () => clearTimeout(timerRef.current as NodeJS.Timeout);
  }, [failureUrl, hasAppliedSecond, canAppliedSecond, startTimeout]);

  return {
    failureUrl,
    companyName,
    hasAppliedSecond,
    provider: formattedProvider,
    showSecondLine,
    errorMessage,
    onClick,
    isLoading,
    isSubmitting,
  };
}
